import { useState, useEffect } from 'react';
import {
    X,
    ChevronRight,
    ChevronLeft,
    Camera,
    Save,
    Plus,
    Maximize2,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    Mic
} from 'lucide-react';
import { elementeApi } from '../services/api';
import { VoiceEnabledTextarea } from './VoiceEnabledTextarea';
import { FotoPanel } from './FotoPanel';
import { WissensPanel } from './WissensPanel';
import type { ElementWithDetails } from '../types/elemente';

interface DoorCaptureWizardProps {
    stockwerkId: number;
    stockwerkName: string;
    /**
     * Bestehendes Element -- schaltet den Wizard in den Bearbeiten-Modus
     * (analog zu GenericElementCaptureWizard.tsx). Ohne dieses Prop wird
     * ein neues Element angelegt wie bisher.
     */
    editElement?: ElementWithDetails | null;
    onClose: () => void;
}

/** Baut den Formular-State aus einem bestehenden Element (Bearbeiten-Modus). */
function buildDoorDataFromElement(element: ElementWithDetails, stockwerkId: number): any {
    const td = (element.technische_daten || {}) as Record<string, any>;
    return {
        typ: 'TÜR',
        stockwerk_id: element.stockwerk_id ?? stockwerkId,
        element_id_intern: element.element_id_intern || '',
        element_id_kunde: element.element_id_kunde || '',
        raum: element.raum || '',
        status: element.status || 'i.O.',
        technische_daten: {
            breite: td.breite ?? '',
            hoehe: td.hoehe ?? '',
            zargentyp: td.zargentyp ?? '',
            gehfluegel: td.gehfluegel ?? '',
            klassifizierung: td.klassifizierung ?? '',
            abz_tuer: td.abz_tuer ?? '',
            abz_fsa: td.abz_fsa ?? '',
            einbauanleitung: td.einbauanleitung ?? false,
            p2: {
                kennzeichnung: td.p2?.kennzeichnung ?? 'i.O.',
                zargeneinbau: td.p2?.zargeneinbau ?? 'i.O.',
                selbstschliessung: td.p2?.selbstschliessung ?? 'i.O.',
                dichtungen: td.p2?.dichtungen ?? 'i.O.',
                beschlaege: td.p2?.beschlaege ?? 'i.O.'
            },
            p2a: {
                fsa: td.p2a?.fsa ?? 'N/A',
                antrieb: td.p2a?.antrieb ?? 'N/A',
                ots: td.p2a?.ots ?? 'N/A',
                fto: td.p2a?.fto ?? 'N/A',
                mk: td.p2a?.mk ?? 'N/A'
            },
            p2b: {
                wandtyp: td.p2b?.wandtyp ?? '',
                wandstaerke: td.p2b?.wandstaerke ?? ''
            },
            maengel: Array.isArray(td.maengel) ? [...td.maengel] : []
        },
        // "bilder" ist reiner UI-Zustand fuer simulierte Foto-Platzhalter
        // (siehe addImage()) und wird von Backend/Element nicht persistiert
        // -- echte Fotos eines bestehenden Elements zeigt im Bearbeiten-
        // Modus FotoPanel (siehe unten), analog zu
        // GenericElementCaptureWizard.tsx.
        bilder: []
    };
}

function buildEmptyDoorData(stockwerkId: number): any {
    return {
        typ: 'TÜR',
        stockwerk_id: stockwerkId,
        element_id_intern: '',
        element_id_kunde: '',
        raum: '',
        status: 'i.O.',
        technische_daten: {
            breite: '',
            hoehe: '',
            zargentyp: '',
            gehfluegel: '',
            klassifizierung: '',
            abz_tuer: '',
            abz_fsa: '',
            einbauanleitung: false,
            p2: {
                kennzeichnung: 'i.O.',
                zargeneinbau: 'i.O.',
                selbstschliessung: 'i.O.',
                dichtungen: 'i.O.',
                beschlaege: 'i.O.'
            },
            p2a: {
                fsa: 'N/A',
                antrieb: 'N/A',
                ots: 'N/A',
                fto: 'N/A',
                mk: 'N/A'
            },
            p2b: {
                wandtyp: '',
                wandstaerke: ''
            },
            maengel: []
        },
        bilder: []
    };
}

const ZARGEN_TYPES = ['Eckzarge', 'Blockzarge', 'Umfassungszarge', 'Stockzarge', 'Stahlzarge', 'Holzzarge'];
const GEHFLUEGEL_TYPES = ['DIN Rechts', 'DIN Links'];
const KLASSIFIZIERUNGEN = ['T30-1-RS', 'T30-2-RS', 'T90-1-RS', 'T90-2-RS', 'T90-1', 'T60', 'Standard-Fluchttür', 'Rauchschutztür'];
const IMAGE_CONTEXTS = ['Gesamtansicht', 'Ü-Schild', 'Zarge/Anschluss', 'Selbstschließung', 'Dichtung', 'Beschlag', 'Feststellanlage', 'Antrieb', 'Wandanschluss', 'Mangel', 'Sonstiges'];

export default function DoorCaptureWizard({ stockwerkId, stockwerkName, editElement, onClose }: DoorCaptureWizardProps) {
    const istBearbeiten = !!editElement?.id;

    const [step, setStep] = useState(1);
    const [showMangelModal, setShowMangelModal] = useState(false);
    const [mangelInput, setMangelInput] = useState('');
    const [doorData, setDoorData] = useState<any>(() =>
        istBearbeiten && editElement
            ? buildDoorDataFromElement(editElement, stockwerkId)
            : buildEmptyDoorData(stockwerkId)
    );

    // Automatische ID-Vergabe (Simulation: T + naechste Nummer) -- nur im
    // Anlage-Modus. Im Bearbeiten-Modus bleibt die bestehende
    // element_id_intern erhalten; sie neu zu berechnen wuerde sie
    // gegebenenfalls aendern, obwohl das Element schon existiert.
    useEffect(() => {
        if (istBearbeiten) return;
        const fetchNextId = async () => {
            try {
                const existing = await elementeApi.getAll();
                const count = existing.filter((e: any) => e.typ === 'TÜR').length + 1;
                setDoorData((prev: any) => ({
                    ...prev,
                    element_id_intern: `T${String(count).padStart(3, '0')}`
                }));
            } catch (e) {
                setDoorData((prev: any) => ({ ...prev, element_id_intern: 'T001' }));
            }
        };
        fetchNextId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [istBearbeiten]);

    const nextStep = () => setStep(s => s + 1);
    const prevStep = () => setStep(s => s - 1);

    const handleP2Change = (key: string, val: string) => {
        setDoorData((prev: any) => ({
            ...prev,
            technische_daten: {
                ...prev.technische_daten,
                p2: { ...prev.technische_daten.p2, [key]: val }
            }
        }));
    };

    const openMangelModal = () => {
        setMangelInput('');
        setShowMangelModal(true);
    };

    const confirmMangel = () => {
        if (mangelInput.trim()) {
            setDoorData((prev: any) => ({
                ...prev,
                technische_daten: {
                    ...prev.technische_daten,
                    maengel: [...prev.technische_daten.maengel, mangelInput.trim()]
                },
                status: 'n.i.O.'
            }));
        }
        setShowMangelModal(false);
        setMangelInput('');
    };

    const addImage = (context: string) => {
        setDoorData((prev: any) => ({
            ...prev,
            bilder: [
                ...prev.bilder,
                { pfad: 'blob:simulated-url-' + Date.now(), kategorie: context }
            ]
        }));
    };

    const handleSave = async (isLoop: boolean) => {
        try {
            if (istBearbeiten && editElement?.id) {
                // Bearbeiten-Modus: bestehendes Element aktualisieren statt
                // ein neues anzulegen. "isLoop" (Kette weiterer Tueren)
                // ergibt hier keinen Sinn -- der Aufrufer zeigt fuer diesen
                // Fall ohnehin nur den "Speichern"-Button an (siehe Step 7).
                await elementeApi.update(editElement.id, doorData);
                onClose();
                return;
            }

            await elementeApi.create(doorData);
            if (isLoop) {
                setStep(1);
                const nextNum = parseInt(doorData.element_id_intern.replace('T', '')) + 1;
                setDoorData({
                    ...buildEmptyDoorData(stockwerkId),
                    element_id_intern: `T${String(nextNum).padStart(3, '0')}`
                });
                alert('Tür gespeichert! Sie können nun die nächste Tür erfassen.');
            } else {
                onClose();
            }
        } catch (err) {
            alert('Fehler beim Speichern');
        }
    };

    return (
        <div className="fixed inset-0 bg-white z-[60] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5">
            {/* Header */}
            <div className="bg-secondary-50 p-6 border-b flex justify-between items-center shrink-0">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-black text-secondary-900">{istBearbeiten ? 'Tür bearbeiten' : 'Tür-Erfassung'}</h2>
                        <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg text-sm font-black tracking-tight">{doorData.element_id_intern}</span>
                    </div>
                    <p className="text-secondary-500 font-bold uppercase tracking-widest text-[10px] mt-1">{stockwerkName}</p>
                </div>
                <button onClick={onClose} className="p-4 bg-white shadow-sm border border-secondary-200 rounded-3xl hover:bg-rose-50 hover:text-rose-600 transition-all active:scale-[0.9]">
                    <X size={24} />
                </button>
            </div>

            {/* Progress */}
            <div className="flex px-6 py-3 gap-1.5 shrink-0">
                {[1, 2, 3, 4, 5, 6, 7].map(s => (
                    <div key={s} className={`h-2 flex-1 rounded-full border-2 border-transparent transition-all ${step >= s ? 'bg-primary-600' : 'bg-secondary-100'}`} />
                ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 md:p-12">
                <div className="max-w-2xl mx-auto space-y-12">

                    {/* STEP 1: BASIS & RAUM */}
                    {step === 1 && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">Identifikation</h3>
                                <p className="text-secondary-500 font-medium">Wo befindet sich die Tür?</p>
                            </div>
                            <div className="grid grid-cols-1 gap-8">
                                <div className="space-y-3">
                                    <label className="text-sm font-black uppercase tracking-widest text-secondary-400">Türname / Inventarnummer</label>
                                    <input
                                        className="w-full p-6 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-2xl font-black focus:border-primary-500 focus:bg-white transition-all outline-none"
                                        placeholder="z.B. HW-01-2024"
                                        value={doorData.element_id_kunde}
                                        onChange={e => setDoorData({ ...doorData, element_id_kunde: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-sm font-black uppercase tracking-widest text-secondary-400">Raum / Standort</label>
                                    <input
                                        className="w-full p-6 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-2xl font-black focus:border-primary-500 focus:bg-white transition-all outline-none"
                                        placeholder="z.B. Flur West / Treppenhaus"
                                        value={doorData.raum}
                                        onChange={e => setDoorData({ ...doorData, raum: e.target.value })}
                                    />
                                </div>
                                <div className="pt-4">
                                    <button className="w-full p-8 border-4 border-dashed border-secondary-200 rounded-[2.5rem] flex flex-col items-center gap-4 text-secondary-400 hover:border-primary-400 hover:text-primary-600 transition-all group">
                                        <Maximize2 size={48} className="group-hover:scale-110 transition-transform" />
                                        <span className="text-xl font-black uppercase tracking-widest">Planverortung festlegen</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: GEOMETRIE & BAUART */}
                    {step === 2 && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">Geometrie</h3>
                                <p className="text-secondary-500 font-medium">Abmessungen und Zargentyp</p>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">Breite (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-6 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-2xl font-black focus:border-primary-500 transition-all outline-none"
                                        value={doorData.technische_daten.breite}
                                        onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, breite: e.target.value } })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">Höhe (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-6 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-2xl font-black focus:border-primary-500 transition-all outline-none"
                                        value={doorData.technische_daten.hoehe}
                                        onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, hoehe: e.target.value } })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-6">
                                <div className="space-y-3">
                                    <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">Zargentyp</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {ZARGEN_TYPES.map(t => (
                                            <button
                                                key={t}
                                                onClick={() => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, zargentyp: t } })}
                                                className={`p-4 rounded-2xl font-black border-4 transition-all ${doorData.technische_daten.zargentyp === t ? 'bg-primary-600 border-primary-600 text-white shadow-xl shadow-primary-200' : 'bg-secondary-50 border-secondary-100 text-secondary-600'}`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">Gehflügel (DIN)</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {GEHFLUEGEL_TYPES.map(t => (
                                            <button
                                                key={t}
                                                onClick={() => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, gehfluegel: t } })}
                                                className={`p-4 rounded-2xl font-black border-4 transition-all ${doorData.technische_daten.gehfluegel === t ? 'bg-primary-600 border-primary-600 text-white shadow-xl shadow-primary-200' : 'bg-secondary-50 border-secondary-100 text-secondary-600'}`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: ZERTIFIZIERUNG */}
                    {step === 3 && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">Klassifizierung</h3>
                                <p className="text-secondary-500 font-medium">Zulassungen und Typen</p>
                            </div>
                            <div className="space-y-6">
                                <div className="space-y-3">
                                    <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">Feuerwiderstand</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {KLASSIFIZIERUNGEN.map(k => (
                                            <button
                                                key={k}
                                                onClick={() => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, klassifizierung: k } })}
                                                className={`p-4 rounded-2xl font-black border-4 text-sm transition-all ${doorData.technische_daten.klassifizierung === k ? 'bg-primary-600 border-primary-600 text-white shadow-xl shadow-primary-200' : 'bg-secondary-50 border-secondary-100 text-secondary-600'}`}
                                            >
                                                {k}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-4 pt-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-secondary-400">AbZ Zulassungsnummer (Tür)</label>
                                        <div className="flex gap-2">
                                            <input
                                                className="flex-1 p-5 bg-secondary-50 border-4 border-secondary-100 rounded-3xl font-black outline-none focus:border-primary-500"
                                                placeholder="Z-6.20-..."
                                                value={doorData.technische_daten.abz_tuer}
                                                onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, abz_tuer: e.target.value } })}
                                            />
                                            <button className="p-5 bg-secondary-100 rounded-3xl text-secondary-600 hover:bg-primary-50 hover:text-primary-600 transition-all">
                                                <Camera size={28} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-secondary-400">AbZ (FSA / Antrieb)</label>
                                        <div className="flex gap-2">
                                            <input
                                                className="flex-1 p-5 bg-secondary-50 border-4 border-secondary-100 rounded-3xl font-black outline-none focus:border-primary-500"
                                                placeholder="Z-..."
                                                value={doorData.technische_daten.abz_fsa}
                                                onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, abz_fsa: e.target.value } })}
                                            />
                                            <button className="p-5 bg-secondary-100 rounded-3xl text-secondary-600 hover:bg-primary-50 hover:text-primary-600 transition-all">
                                                <Camera size={28} />
                                            </button>
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-4 p-6 bg-secondary-50 rounded-3xl border-4 border-secondary-100 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            className="w-8 h-8 rounded-xl border-4 text-primary-600 focus:ring-primary-500"
                                            checked={doorData.technische_daten.einbauanleitung}
                                            onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, einbauanleitung: e.target.checked } })}
                                        />
                                        <span className="text-xl font-bold text-secondary-900">Einbauanleitung vorhanden</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: PRÜFUNG P2 */}
                    {step === 4 && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">Prüfung P2</h3>
                                <p className="text-secondary-500 font-medium">Element-Bewertung</p>
                            </div>
                            {/* Hinweise aus der Wissensdatenbank zum Pruefbereich
                                TUER -- gerade hier, waehrend der eigentlichen
                                Vor-Ort-Pruefung, ist der Zugriff am wertvollsten. */}
                            <WissensPanel art="PRUEFBEREICH" ziel="TUER" />
                            <div className="space-y-4">
                                {[
                                    { label: 'Kennzeichnung (Ü-Schild)', key: 'kennzeichnung' },
                                    { label: 'Zargeneinbau / Hinterfüllung', key: 'zargeneinbau' },
                                    { label: 'Selbstschließung', key: 'selbstschliessung' },
                                    { label: 'Dichtungen (Sitz/Zustand)', key: 'dichtungen' },
                                    { label: 'Beschläge (Schloss/Drücker)', key: 'beschlaege' }
                                ].map(item => (
                                    <div key={item.key} className="p-6 bg-secondary-50 rounded-[2.5rem] border-4 border-secondary-100 flex flex-col gap-4">
                                        <span className="text-xl font-black text-secondary-900">{item.label}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleP2Change(item.key, 'i.O.')}
                                                className={`flex-1 py-4 rounded-2xl font-black border-4 transition-all ${doorData.technische_daten.p2[item.key] === 'i.O.' ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-secondary-200 text-secondary-400'}`}
                                            >
                                                i.O.
                                            </button>
                                            <button
                                                onClick={() => handleP2Change(item.key, 'n.i.O.')}
                                                className={`flex-1 py-4 rounded-2xl font-black border-4 transition-all ${doorData.technische_daten.p2[item.key] === 'n.i.O.' ? 'bg-rose-600 border-rose-600 text-white' : 'bg-white border-secondary-200 text-secondary-400'}`}
                                            >
                                                n.i.O.
                                            </button>
                                            <button
                                                className="p-4 bg-white border-4 border-secondary-200 rounded-2xl text-secondary-400"
                                                onClick={() => addImage(item.label)}
                                            >
                                                <Camera size={24} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 5: PRÜFUNG P2a/b (Zusätzliches) */}
                    {step === 5 && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">P2a / P2b</h3>
                                <p className="text-secondary-500 font-medium">FSA, Antriebe und Wandanschluss</p>
                            </div>
                            <div className="space-y-6">
                                <div className="p-8 bg-white border-4 border-primary-50 rounded-[2.5rem] space-y-6">
                                    <h4 className="text-xl font-black text-primary-600 mb-4">Zusatzausstattung</h4>
                                    {['fsa', 'antrieb', 'ots', 'fto', 'mk'].map(k => (
                                        <div key={k} className="flex items-center justify-between">
                                            <span className="font-bold text-secondary-700 uppercase tracking-widest text-sm">{k.toUpperCase()}</span>
                                            <div className="flex gap-1 bg-secondary-100 p-1 rounded-xl">
                                                {['i.O.', 'n.i.O.', 'N/A'].map(v => (
                                                    <button
                                                        key={v}
                                                        onClick={() => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, p2a: { ...doorData.technische_daten.p2a, [k]: v } } })}
                                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${doorData.technische_daten.p2a[k] === v ? 'bg-primary-600 text-white' : 'text-secondary-400 hover:bg-secondary-200'}`}
                                                    >
                                                        {v}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="p-8 bg-secondary-50 border-4 border-secondary-100 rounded-[2.5rem] space-y-6">
                                    <h4 className="text-xl font-black text-secondary-900">Wandanschluss</h4>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-secondary-500 ml-2">Wandtyp (Trockenbau, Beton, etc.)</label>
                                            <input
                                                className="w-full p-4 bg-white border-2 border-secondary-200 rounded-2xl font-bold"
                                                value={doorData.technische_daten.p2b.wandtyp}
                                                onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, p2b: { ...doorData.technische_daten.p2b, wandtyp: e.target.value } } })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-secondary-500 ml-2">Wandstärke (mm)</label>
                                            <input
                                                className="w-full p-4 bg-white border-2 border-secondary-200 rounded-2xl font-bold"
                                                value={doorData.technische_daten.p2b.wandstaerke}
                                                onChange={e => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, p2b: { ...doorData.technische_daten.p2b, wandstaerke: e.target.value } } })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 6: BILDER & MÄNGEL */}
                    {step === 6 && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-8">
                            <div className="space-y-2">
                                <h3 className="text-4xl font-black text-secondary-900 tracking-tight">Dokumentation</h3>
                                <p className="text-secondary-500 font-medium">Bilder und Mängelauflistung</p>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-xl font-black text-secondary-900">Mängel ({doorData.technische_daten.maengel.length})</h4>
                                        <button
                                            onClick={openMangelModal}
                                            className="bg-rose-50 text-rose-600 px-6 py-3 rounded-2xl font-black text-sm flex items-center gap-2 border-2 border-rose-100 hover:bg-rose-100 transition-all"
                                        >
                                            <Plus size={18} /> Mangel erfassen
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {doorData.technische_daten.maengel.length === 0 ? (
                                            <div className="p-8 bg-emerald-50 rounded-[2rem] border-2 border-emerald-100 flex items-center gap-4 text-emerald-700 font-bold">
                                                <CheckCircle2 size={32} />
                                                Keine Mängel erfasst
                                            </div>
                                        ) : (
                                            doorData.technische_daten.maengel.map((m: string, i: number) => (
                                                <div key={i} className="p-5 bg-rose-50 border-2 border-rose-100 rounded-2xl flex justify-between items-center">
                                                    <div className="flex gap-3">
                                                        <AlertTriangle className="text-rose-500 shrink-0" size={20} />
                                                        <span className="font-bold text-rose-900">{m}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => setDoorData({ ...doorData, technische_daten: { ...doorData.technische_daten, maengel: doorData.technische_daten.maengel.filter((_: any, idx: number) => idx !== i) } })}
                                                        className="text-rose-400 hover:text-rose-600"
                                                    >
                                                        <Trash2 size={20} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <hr className="border-secondary-100" />

                                <div className="space-y-4">
                                    <h4 className="text-xl font-black text-secondary-900">Fotos (1:N)</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => addImage('Gesamtansicht')}
                                            className="aspect-square bg-secondary-50 border-4 border-dashed border-secondary-200 rounded-[2rem] flex flex-col items-center justify-center gap-2 text-secondary-400 hover:text-primary-600 hover:border-primary-300 transition-all"
                                        >
                                            <Camera size={32} />
                                            <span className="text-[10px] font-black uppercase">Foto hinzufügen</span>
                                        </button>
                                        {doorData.bilder.map((img: any, i: number) => (
                                            <div key={i} className="aspect-square bg-secondary-100 rounded-[2rem] relative overflow-hidden group">
                                                <div className="absolute inset-0 bg-primary-600/10 flex items-center justify-center font-black text-primary-700 text-[10px] uppercase p-4 text-center">
                                                    {img.kategorie}
                                                </div>
                                                <button
                                                    className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur rounded-xl text-rose-500 opacity-0 group-hover:opacity-100 transition-all"
                                                    onClick={() => setDoorData({ ...doorData, bilder: doorData.bilder.filter((_: any, idx: number) => idx !== i) })}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <select
                                                        className="w-full bg-white/90 backdrop-blur rounded-xl text-[10px] font-black p-2 border-none ring-0 appearance-none text-secondary-900 outline-none"
                                                        value={img.kategorie}
                                                        onChange={(e) => {
                                                            const newBilder = [...doorData.bilder];
                                                            newBilder[i].kategorie = e.target.value;
                                                            setDoorData({ ...doorData, bilder: newBilder });
                                                        }}
                                                    >
                                                        {IMAGE_CONTEXTS.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Echte Fotos eines bestehenden Elements --
                                    nur im Bearbeiten-Modus: die Kacheln oben
                                    ("Fotos (1:N)") sind reine UI-Simulation
                                    ohne echten Upload (siehe addImage()) und
                                    werden nicht gespeichert. FotoPanel haengt
                                    dagegen am echten Foto-Endpunkt und
                                    braucht dafuer eine bereits vorhandene
                                    elementId -- analog zu
                                    GenericElementCaptureWizard.tsx. */}
                                {istBearbeiten && editElement?.id && (
                                    <>
                                        <hr className="border-secondary-100" />
                                        <div className="space-y-4">
                                            <h4 className="text-xl font-black text-secondary-900">Gespeicherte Fotos</h4>
                                            <FotoPanel elementId={editElement.id} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* STEP 7: FINISH & LOOP */}
                    {step === 7 && (
                        <div className="space-y-12 py-10 animate-in fade-in zoom-in-95">
                            <div className="bg-primary-50 p-12 rounded-[3.5rem] border-4 border-primary-100 text-center space-y-6">
                                <div className="w-28 h-28 bg-primary-600 rounded-[2.5rem] flex items-center justify-center mx-auto shadow-2xl shadow-primary-200 rotate-3">
                                    <CheckCircle2 size={60} className="text-white" />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-4xl font-black text-secondary-900">
                                        {istBearbeiten ? 'Änderungen bereit!' : 'Erfassung bereit!'}
                                    </h3>
                                    <p className="text-secondary-600 text-xl font-medium">
                                        {istBearbeiten
                                            ? <>Alle Änderungen an <b>{doorData.element_id_intern}</b> wurden erfasst.</>
                                            : <>Alle Daten für <b>{doorData.element_id_intern}</b> wurden strukturiert erfasst.</>}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6">
                                {!istBearbeiten && (
                                    <button
                                        onClick={() => handleSave(true)}
                                        className="w-full py-8 bg-primary-600 text-white rounded-[2.5rem] font-black text-2xl shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] active:scale-[0.98] hover:scale-[1.02] transition-all flex items-center justify-center gap-4"
                                    >
                                        <Plus size={32} />
                                        Nächste Tür erfassen
                                    </button>
                                )}
                                <button
                                    onClick={() => handleSave(false)}
                                    className={
                                        istBearbeiten
                                            ? 'w-full py-8 bg-primary-600 text-white rounded-[2.5rem] font-black text-2xl shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] active:scale-[0.98] hover:scale-[1.02] transition-all flex items-center justify-center gap-4'
                                            : 'w-full py-8 bg-white border-4 border-secondary-200 text-secondary-900 rounded-[2.5rem] font-black text-2xl active:scale-[0.98] hover:bg-secondary-50 transition-all flex items-center justify-center gap-4'
                                    }
                                >
                                    <Save size={32} />
                                    {istBearbeiten ? 'Änderungen speichern' : 'Erfassung beenden'}
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Footer Navigation (only for steps < 7) */}
            {step < 7 && (
                <div className="p-6 bg-white border-t flex gap-4 shrink-0">
                    {step > 1 && (
                        <button
                            onClick={prevStep}
                            className="flex-1 py-6 bg-secondary-100 text-secondary-700 rounded-3xl font-black text-xl flex items-center justify-center gap-2 hover:bg-secondary-200 active:scale-[0.95] transition-all"
                        >
                            <ChevronLeft size={24} />
                            Zurück
                        </button>
                    )}
                    <button
                        onClick={nextStep}
                        className="flex-[2] py-6 bg-primary-600 text-white rounded-3xl font-black text-xl flex items-center justify-center gap-2 shadow-xl shadow-primary-100 hover:scale-[1.02] active:scale-[0.95] transition-all disabled:opacity-50"
                    >
                        {step === 6 ? 'Zur Zusammenfassung' : 'Weiter'}
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}

            {/* Mangel Input Modal with Voice Support */}
            {showMangelModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm animate-in zoom-in-95 duration-200">
                    <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-secondary-100 bg-rose-50/50">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold text-secondary-900 flex items-center gap-2">
                                        <AlertTriangle className="text-rose-500" size={24} />
                                        Mangel erfassen
                                    </h3>
                                    <p className="text-sm text-secondary-500 mt-1">
                                        Beschreiben Sie den festgestellten Mangel
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setShowMangelModal(false); setMangelInput(''); }}
                                    className="p-2 hover:bg-rose-100 rounded-xl text-secondary-400 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-secondary-700 mb-2 flex items-center gap-2">
                                    Mangelbeschreibung
                                    <span className="text-xs font-normal text-secondary-400 flex items-center gap-1">
                                        <Mic size={12} /> Spracheingabe verfügbar
                                    </span>
                                </label>
                                <VoiceEnabledTextarea
                                    value={mangelInput}
                                    onChange={(e) => setMangelInput(e.target.value)}
                                    className="w-full p-4 bg-secondary-50 border-2 border-secondary-200 rounded-2xl min-h-[120px] resize-none focus:border-primary-500 focus:bg-white transition-all outline-none font-medium"
                                    placeholder="z.B. Dichtung defekt, Selbstschließung nicht funktionsfähig..."
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { setShowMangelModal(false); setMangelInput(''); }}
                                    className="flex-1 py-4 bg-secondary-100 text-secondary-700 rounded-2xl font-bold hover:bg-secondary-200 transition-all"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={confirmMangel}
                                    disabled={!mangelInput.trim()}
                                    className="flex-1 py-4 bg-rose-500 text-white rounded-2xl font-bold hover:bg-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    <Plus size={20} />
                                    Mangel hinzufügen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
